import { useMemo } from 'react'
import { Activity } from '../../../shared/queries/useGetActivitiesQuery'
import { TppMatrixState } from '../types'

interface TppGanttSection {
    id: string
    title: string
}

interface TppGanttData {
    activitiesBySection: Record<string, Activity[]>
    sections: TppGanttSection[]
    allLinkedActivityIds: string[]
    isLoading: boolean
}

export const useTppGanttData = (
    matrixState: TppMatrixState | null,
    activities: Activity[] | undefined,
    isActivitiesLoading: boolean,
    selectedColumnId?: string | null,
    drillDownSectionId?: string | null,
    highlightedOnly?: boolean
): TppGanttData => {
    return useMemo(() => {
        if (!matrixState || !activities) {
            return {
                activitiesBySection: {},
                sections: [],
                allLinkedActivityIds: [],
                isLoading: isActivitiesLoading,
            }
        }

        // Build a fast lookup from activity ID -> Activity object
        const activitiesById: Record<string, Activity> = {}
        activities.forEach((a) => {
            activitiesById[a._id] = a
        })

        // Single pass: for each cell, resolve the section and place linked activities there directly
        const activitiesBySection: Record<string, Activity[]> = {}
        const allLinkedActivityIds = new Set<string>()

        // Track which (activityId, sectionId) pairs have been added to avoid duplicates
        // when the same activity links to multiple cells within the same section
        const addedPairs = new Set<string>()

        Object.entries(matrixState.cellLookup).forEach(([slotId, columnCells]) => {
            const entries = selectedColumnId
                ? columnCells[selectedColumnId]
                    ? [[selectedColumnId, columnCells[selectedColumnId]] as const]
                    : []
                : Object.entries(columnCells)
            entries.forEach(([, cell]) => {
                if (!cell.linkedActivities?.length) return
                if (highlightedOnly && !cell.isHighlighted) return

                // Get slot -> row -> section
                const slot = matrixState.slots[slotId]
                if (!slot) return

                const row = matrixState.rows[slot.rowId]
                if (!row) return

                const sectionId = row.sectionId

                cell.linkedActivities.forEach((activityId) => {
                    allLinkedActivityIds.add(activityId)

                    const pairKey = `${activityId}__${sectionId}`
                    if (addedPairs.has(pairKey)) return
                    addedPairs.add(pairKey)

                    const activity = activitiesById[activityId]
                    if (!activity) return

                    if (!activitiesBySection[sectionId]) {
                        activitiesBySection[sectionId] = []
                    }
                    activitiesBySection[sectionId].push(activity)
                })
            })
        })

        const linkedActivityIdsArray = Array.from(allLinkedActivityIds)

        // Build ordered sections list (only sections with activities)
        const sectionIds = matrixState.tpp?.sectionIds || []
        const sections: TppGanttSection[] = sectionIds
            .filter((sectionId) => activitiesBySection[sectionId]?.length > 0)
            .map((sectionId) => ({
                id: sectionId,
                title: matrixState.sections[sectionId]?.title || 'Unknown Section',
            }))

        // If drilled down into a section, filter to only that section
        if (drillDownSectionId) {
            const filteredSections = sections.filter((s) => s.id === drillDownSectionId)
            const filteredActivities: Record<string, Activity[]> = {}
            if (activitiesBySection[drillDownSectionId]) {
                filteredActivities[drillDownSectionId] = activitiesBySection[drillDownSectionId]
            }
            return {
                activitiesBySection: filteredActivities,
                sections: filteredSections,
                allLinkedActivityIds: linkedActivityIdsArray,
                isLoading: isActivitiesLoading,
            }
        }

        return {
            activitiesBySection,
            sections,
            allLinkedActivityIds: linkedActivityIdsArray,
            isLoading: isActivitiesLoading,
        }
    }, [matrixState, activities, isActivitiesLoading, selectedColumnId, drillDownSectionId, highlightedOnly])
}
